import { useRef, useState } from "react"
import { CheckCircle2, Loader2, PencilLine } from "lucide-react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogDescription,
  ResponsiveDialogFooter,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
} from "@/components/ui/responsive-dialog"

/**
 * The draft the member reads before it goes to their coach.
 *
 * There is deliberately no "start over": every answer is already saved, so
 * starting over would mean deleting work the member did. Asking for a change is
 * the path back, and after three of those the note goes to a person instead.
 */
export function DraftReview({
  draft,
  memberFirstName,
  coachName,
  rewritesRemaining,
  rewriting,
  rewriteFailed = false,
  noteSentToCoach = false,
  sending,
  sent,
  error,
  onRequestChange,
  onSend,
}) {
  const [changeOpen, setChangeOpen] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [note, setNote] = useState("")
  const [consent, setConsent] = useState(false)
  const headshotRef = useRef(null)

  const submitChange = () => {
    if (!note.trim()) return
    onRequestChange(note.trim())
    setNote("")
    setChangeOpen(false)
  }

  const submitSend = () => {
    if (!consent) return
    onSend({ consent: true, headshot: headshotRef.current?.files?.[0] || null })
  }

  if (sent) {
    return (
      <div className="mx-auto w-full max-w-2xl px-6 py-16 text-center">
        <CheckCircle2 className="mx-auto h-10 w-10 text-kf-red" aria-hidden="true" />
        <h1 className="mt-5 font-display text-3xl font-bold text-zinc-900">
          Thank you, {memberFirstName}.
        </h1>
        <p className="mx-auto mt-4 max-w-md text-sm leading-relaxed text-zinc-600">
          Your story is with {coachName} now. They will edit it and submit it for
          review — you will hear from them before it is published anywhere.
        </p>
        <Button variant="kf-outline" className="mt-8" asChild>
          <a href="/app/member">Back to the member portal</a>
        </Button>
      </div>
    )
  }

  return (
    <div className="mx-auto w-full max-w-2xl px-6 py-8">
      <div className="flex items-center justify-between gap-3">
        <h1 className="font-display text-2xl font-bold text-zinc-900">Your draft</h1>
        <Badge variant="secondary">Not yet published</Badge>
      </div>

      <p className="mt-3 text-sm leading-relaxed text-zinc-600">
        Nice work, {memberFirstName}. Here is a first pass, built from what you
        just told us — {coachName} will polish it before it goes anywhere.
      </p>

      <article className="mt-6 rounded-lg border border-zinc-200 bg-white p-6 shadow-sm">
        <p className="font-mono text-xs uppercase tracking-wider text-kf-red">
          Member Stories · Draft {draft.version > 1 && `v${draft.version}`}
        </p>
        <h2 className="mt-2 font-display text-2xl font-bold leading-snug text-zinc-900">
          {draft.title}
        </h2>
        <p className="mt-2 text-xs text-zinc-500">As told to Kingdom Factor</p>

        <div
          className="prose prose-zinc mt-6 max-w-none text-sm leading-relaxed prose-blockquote:border-l-kf-red prose-headings:font-display"
          // The body is HTML written by our own draft prompt and stored on the
          // Article — the same content the coach's editor renders.
          dangerouslySetInnerHTML={{ __html: draft.content }}
        />
      </article>

      {noteSentToCoach && (
        <p className="mt-4 rounded-md border-l-2 border-emerald-500 bg-emerald-50 p-3 text-sm text-emerald-800">
          Your note is with {coachName}. They will see it beside your story when
          they edit it.
        </p>
      )}

      {rewriteFailed && (
        <p className="mt-4 rounded-md border-l-2 border-amber-500 bg-amber-50 p-3 text-sm text-amber-900">
          That rewrite is taking longer than it should. The draft below is
          unchanged — you can ask again, or send it to {coachName} as it is and
          tell them what to fix.
        </p>
      )}

      {error && <p className="mt-4 text-sm text-kf-red">{error}</p>}

      <div className="mt-6 flex flex-col gap-3">
        <Button
          variant="gradient"
          size="lg"
          disabled={rewriting || sending}
          onClick={() => setConfirmOpen(true)}
        >
          Looks good — send to my coach
        </Button>

        <Button
          variant="kf-outline"
          size="lg"
          disabled={rewriting || sending}
          onClick={() => setChangeOpen(true)}
        >
          {rewriting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" /> Rewriting…
            </>
          ) : (
            <>
              <PencilLine className="h-4 w-4" /> Ask for a change first
            </>
          )}
        </Button>

        <p className="text-center text-xs text-zinc-500">
          {rewritesRemaining > 0
            ? `${rewritesRemaining} ${rewritesRemaining === 1 ? "change" : "changes"} left before your note goes straight to ${coachName}.`
            : `You have used your changes — anything else you write goes to ${coachName} as a note.`}
        </p>
      </div>

      {/* Ask for a change */}
      <ResponsiveDialog open={changeOpen} onOpenChange={setChangeOpen}>
        <ResponsiveDialogContent>
          <ResponsiveDialogHeader>
            <ResponsiveDialogTitle>What would you like changed?</ResponsiveDialogTitle>
            <ResponsiveDialogDescription>
              {rewritesRemaining > 0
                ? "Be as specific as you like — we will change what you ask for and leave the rest alone."
                : `We have rewritten this as many times as we can. Your note will go to ${coachName} instead.`}
            </ResponsiveDialogDescription>
          </ResponsiveDialogHeader>

          <div className="px-4 pb-2 md:px-0">
            <Label htmlFor="change-note" className="sr-only">
              Your note
            </Label>
            <Textarea
              id="change-note"
              rows={4}
              value={note}
              onChange={(event) => setNote(event.target.value)}
              placeholder="e.g. My company is Ironclad Fabrication, not Ironclad Fabricators."
              className="border-zinc-300 bg-white text-zinc-900"
            />
          </div>

          <ResponsiveDialogFooter>
            <Button variant="gradient" disabled={!note.trim()} onClick={submitChange}>
              {rewritesRemaining > 0 ? "Rewrite it" : `Send the note to ${coachName}`}
            </Button>
          </ResponsiveDialogFooter>
        </ResponsiveDialogContent>
      </ResponsiveDialog>

      {/* Consent + headshot */}
      <ResponsiveDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <ResponsiveDialogContent>
          <ResponsiveDialogHeader>
            <ResponsiveDialogTitle>Send your story to {coachName}?</ResponsiveDialogTitle>
            <ResponsiveDialogDescription>
              They will edit it and submit it for review. Nothing is published
              until it has been approved.
            </ResponsiveDialogDescription>
          </ResponsiveDialogHeader>

          <div className="space-y-5 px-4 pb-2 md:px-0">
            <label className="flex cursor-pointer items-start gap-3 text-sm leading-relaxed text-zinc-700">
              <input
                type="checkbox"
                checked={consent}
                onChange={(event) => setConsent(event.target.checked)}
                className="mt-0.5 h-4 w-4 shrink-0 accent-kf-red"
              />
              <span>
                I am happy for Kingdom Factor to publish my story, including my
                name and my business.
              </span>
            </label>

            <div className="space-y-2">
              <Label htmlFor="headshot">Add a photo of yourself (optional)</Label>
              <input
                ref={headshotRef}
                id="headshot"
                type="file"
                accept="image/*"
                className="block w-full text-sm text-zinc-600 file:mr-3 file:rounded-md file:border-0 file:bg-zinc-100 file:px-3 file:py-2 file:text-sm file:font-medium file:text-zinc-700 hover:file:bg-zinc-200"
              />
            </div>
          </div>

          <ResponsiveDialogFooter>
            <Button variant="gradient" disabled={!consent || sending} onClick={submitSend}>
              {sending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Sending…
                </>
              ) : (
                "Send to my coach"
              )}
            </Button>
          </ResponsiveDialogFooter>
        </ResponsiveDialogContent>
      </ResponsiveDialog>
    </div>
  )
}
